import * as React from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core';
import { isMobile } from 'react-device-detect';

/* import icons */
import CustomFade from '../../helpers/CustomFade';
import { PanZoomContext } from './PanZoomContext';
import {
  shortCutFullscreen,
  useImagePreviewKeyboardShortCuts
} from './hooks/useImagePreviewKeyboardShortCuts';
import { Button } from '@cube3/cubicle/src/core/atoms/Button/Button';
import { Tooltip } from '@cube3/cubicle/src/core/atoms/Tooltip/Tooltip';
import { ZoomButtons } from './ZoomButtons';
import { SizeVariants } from '@cube3/cubicle/src/theme/themes';
import { useCurrentShareLink } from '../../../../state/src/redux/components/Hooks/useCurrentShareLink';
import { AddCommentButton } from '../VideoPreview/AddCommentButton';
import { useCallback } from 'react';


const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    mobile: {},
    container: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-around',
      alignItems: 'center',
      flexWrap: 'wrap',
      paddingBottom: theme.customSpacing.padding[20],
      paddingLeft: theme.customSpacing.padding[24],
      paddingRight: theme.customSpacing.padding[24],
      paddingTop: theme.customSpacing.padding[32],
      '&$mobile': {
        padding: theme.customSpacing.padding[8]
      }
    },
    scrim: {
      pointerEvents: 'none',
      position: 'absolute',
      height: '92px', // 20 bottom , 40 middle (content), 32 top
      left: 0,
      bottom: 0,
      right: 0,
      background: theme.customPalette.surfaceAccent.onBase.scrim3,
      zIndex: -1,
      '$mobile &': {
        height: theme.sizing[48]
      }
    },
    barGroup: {
      display: 'flex',
      flexFlow: 'row nowrap',
      justifyContent: isMobile ? 'flex-end' : 'space-between',
      alignItems: 'center',
      textAlign: 'center',
      width: '100%'
    },
    zoomButtonContainer: {
      display: 'flex',
      margin: 'auto',
      justifySelf: 'center',
      width: theme.sizing[144],
      justifyContent: 'space-between',
      paddingLeft: theme.customSpacing.padding[40]
    },
    fullScreenbutton: {
      justifySelf: 'flex-end',
      color: theme.customPalette.primary.textWhiteHighEmphasis
    },

    transparentBackground: {
      '&:hover': {
        backgroundColor: theme.customPalette.primary.lineWhiteMediumEmphasis
      }
    }
  })
);

interface ImagePreviewControlsProps {
  onCopy?: () => void;
  toggleFullscreenAction: () => void;
  fullscreen: boolean;
  renderFullscreenControls?: () => React.ReactNode;
  toggleDrawingBar?: () => void;
}

const ImagePreviewControls: React.FC<ImagePreviewControlsProps> = (props) => {
  const {
    onCopy,
    toggleFullscreenAction,
    fullscreen,
    renderFullscreenControls,
    toggleDrawingBar
  } = props;

  const { zoomIn, zoomOut, zoomReset, canZoomIn, canZoomOut, canFitZoom } =
    React.useContext(PanZoomContext);

  useImagePreviewKeyboardShortCuts({
    zoomIn,
    zoomOut,
    zoomReset,
    canZoomIn,
    canZoomOut,
    canFitZoom,
    toggleFullscreenAction
  });

  const classes = useStyles();

  //if review or share show comment button
  const typeOfView = useCurrentShareLink()?.type;
  const useComment = typeOfView === 'share';

  const handleClickDrawingBar = useCallback(() => {
    toggleDrawingBar();

  }, [toggleDrawingBar]);


  return (
    <div className={`${classes.container} ${isMobile ? classes.mobile : ''}`}>
      {fullscreen && renderFullscreenControls()}

      <div className={classes.scrim} />
      <div
        className={classes.barGroup}
        data-set="preventDeselect" // prevent review comment from being deselected due to clicking zoom buttons
      >
        {!isMobile &&
          <div className={classes.zoomButtonContainer}>
            {onCopy && (
              <Tooltip content={isMobile ? '' : 'Copy'} placement="bottom">
                <Button
                  iconLeft={'copy'}
                  background="secondary"
                  buttonStyle="ghost"
                  onClick={onCopy}
                />
              </Tooltip>
            )}
            <ZoomButtons />
          </div>
        }
        {isMobile && useComment &&
          <>
            <Button
              aria-label="toggle drawing bar"
              iconLeft={'gesture'}
              background="secondary"
              buttonStyle="ghost"
              onClick={handleClickDrawingBar}
              size={SizeVariants.lg}
            />
            <AddCommentButton />
          </>
        }

        <>
          <Tooltip content={isMobile ? '' : makeTip(shortCutFullscreen)} placement="bottom">
            <Button
              iconLeft={fullscreen ? 'collapse_content' : 'expand_content'}
              background="secondary"
              buttonStyle="ghost"
              size={isMobile ? SizeVariants.lg : SizeVariants.md}
              onClick={toggleFullscreenAction}
            />
          </Tooltip>
        </>
      </div>
    </div>
  );
};

export const makeTip = (sc) => `${sc.help} (${sc.tip})`;

export default CustomFade(ImagePreviewControls);
const ImagePreviewControlsMobile = ImagePreviewControls;
export { ImagePreviewControlsMobile };
